import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import Autocomplete from "@mui/material/Autocomplete";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { SyntheticEvent, UIEvent } from "react";
import { useController } from "react-hook-form";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { CustomPaper, CustomPopper } from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { Spinner, useStyles } from "@/main/components/shared/inputAutoComplete/styles";
import KogInputText from "@/main/components/shared/inputText/inputText";
import type { KeyValue } from "@/main/models/common";
import type { PaginationResponse } from "@/main/models/common/paginationResponse";
import type { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getTranslateByKey } from "@/main/services/utils";

import {
	includeSelectedOption,
	isKeyValue,
	isSameOption,
	isScrolledToBottom,
} from "./autoCompleteSearchControlled.utils";

const SEARCH_DEBOUNCE_TIME = 600;

export type AutoCompleteSearchPage = Pick<PaginationResponse<KeyValue>, "results" | "totalNumberOfRecords">;

interface IAutoCompleteSearchControlledProps<T extends FieldValues> {
	id: string;
	name: FieldPath<T>;
	control: Control<T>;
	label: string;
	loadingText: string;
	getData: (request: SearchTermRequest, signal: AbortSignal) => Promise<AutoCompleteSearchPage>;
}

export function AutoCompleteSearchControlled<T extends FieldValues>({
	id,
	name,
	control,
	label,
	loadingText,
	getData,
}: Readonly<IAutoCompleteSearchControlledProps<T>>) {
	const classes = useStyles();
	const { field } = useController({ name, control });
	const [searchText, setSearchText] = useState("");
	const [options, setOptions] = useState<KeyValue[]>([]);
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const controllerRef = useRef<AbortController | null>(null);

	const fieldValue: unknown = field.value;
	const selectedOption = isKeyValue(fieldValue) ? fieldValue : null;
	const hasMorePages = options.length < totalRecords;

	async function loadPage(text: string, page: number) {
		controllerRef.current?.abort();
		const controller = new AbortController();
		controllerRef.current = controller;
		setIsLoading(true);

		try {
			const { results, totalNumberOfRecords } = await getData(
				{ searchTerm: text, pageIndex: page, pageSize: PaginationEnum.PageSize10 },
				controller.signal,
			);
			setOptions((previous) => (page === PaginationEnum.PageIndexIntial ? results : [...previous, ...results]));
			setPageIndex(page);
			setTotalRecords(totalNumberOfRecords);
		} catch {
			if (!controller.signal.aborted && page === PaginationEnum.PageIndexIntial) {
				setOptions([]);
				setTotalRecords(0);
			}
		} finally {
			if (controllerRef.current === controller) {
				setIsLoading(false);
			}
		}
	}

	const loadFirstPage = useEffectEvent((text: string) => {
		loadPage(text, PaginationEnum.PageIndexIntial);
	});

	useEffect(() => {
		const timeout = setTimeout(() => loadFirstPage(searchText), searchText ? SEARCH_DEBOUNCE_TIME : 0);
		return () => {
			clearTimeout(timeout);
			controllerRef.current?.abort();
		};
	}, [searchText]);

	function handleInputChange(_event: SyntheticEvent, inputText: string, reason: AutocompleteInputChangeReason) {
		setSearchText(reason === "input" ? inputText.trim() : "");
	}

	function handleListboxScroll(event: UIEvent<HTMLElement>) {
		if (isLoading || !hasMorePages || !isScrolledToBottom(event.currentTarget)) {
			return;
		}
		loadPage(searchText, pageIndex + 1);
	}

	return (
		<Autocomplete
			id={id}
			className={`${classes.inputAutoComplete} ignore-click`}
			size="small"
			openOnFocus
			value={selectedOption}
			options={includeSelectedOption(options, selectedOption)}
			loading={isLoading}
			loadingText={loadingText}
			noOptionsText={getTranslateByKey("information.noRecordsFound")}
			popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
			filterOptions={(currentOptions) => currentOptions}
			getOptionKey={(option) => String(option.key)}
			getOptionLabel={(option) => option.value}
			isOptionEqualToValue={isSameOption}
			onChange={(_event, option) => field.onChange(option)}
			onInputChange={handleInputChange}
			onBlur={field.onBlur}
			slots={{ paper: CustomPaper, popper: CustomPopper }}
			slotProps={{ listbox: { onScroll: handleListboxScroll } }}
			renderInput={(params) => (
				<KogInputText label={label} title={selectedOption?.value ?? ""} variant="filled" {...params} />
			)}
		/>
	);
}
